import { breakpointSizePx, BreakpointSize } from '@jbhi-fi/lanyard-ui/themes';
import { useCurrentBreakpoint } from '../../context/CurrentBreakpointProvider';
import { DeepPartial } from '@jbhi-fi/utils/src/DeepPartial';
import deepmerge from 'ts-deepmerge';
import { RefObject, useMemo } from 'react';
import { getBreakpointValue } from '@jbhi-fi/utils/src/breakpointHelpers/breakpointHelpers';
import { useContainerWidth } from '@jbhi-fi/lanyard-ui/utils/useContainerWidth';

const BREAKPOINT_ORDER: Array<Breakpoint> = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'];

type Breakpoint = BreakpointSize;
export type ResponsiveValue<T> = Partial<Record<Breakpoint, DeepPartial<T>>>;
export type PossiblyResponsiveValue<T> = ResponsiveValue<T> | T;

const isObject = (value: any): value is object => {
  return value && !Array.isArray(value) && typeof value === 'object';
};

const isResponsiveValue = <T>(value: PossiblyResponsiveValue<T>): value is ResponsiveValue<T> => {
  return isObject(value) && Object.keys(value).every(k => BREAKPOINT_ORDER.includes(k as Breakpoint));
};

const toResponsiveValue = <T>(value: PossiblyResponsiveValue<T>): ResponsiveValue<T> => {
  return isResponsiveValue(value) ? value : { xs: value as DeepPartial<T> };
};

export const breakpointToScreenSize = (breakpoint: Breakpoint): number => {
  return breakpointSizePx[breakpoint] ?? Number.MAX_SAFE_INTEGER;
};

export const useResponsiveValue = <T>(initialValue: PossiblyResponsiveValue<T>, ref?: RefObject<HTMLElement>): T => {
  const currentBreakpoint = useCurrentBreakpoint();
  const responsiveValue = useMemo(() => toResponsiveValue(initialValue), [initialValue]);

  const containerWidth = useContainerWidth(ref);
  const effectiveBreakpointValue = ref ? breakpointSizePx[getBreakpointValue(containerWidth)] : currentBreakpoint.value;
  return useMemo(() => {
    return BREAKPOINT_ORDER.reduce<T>((acc, bp) => {
      const currentValue = responsiveValue[bp];
      if (currentValue !== undefined && effectiveBreakpointValue >= breakpointToScreenSize(bp)) {
        if (isObject(currentValue)) {
          return deepmerge.withOptions({ mergeArrays: false }, acc ?? {}, currentValue) as T;
        }
        return currentValue as T;
      }
      return acc;
    }, undefined as T);
  }, [effectiveBreakpointValue, responsiveValue]);
};
